import React, { useState } from 'react';
import {
    Button,
    Input,
    message,
    Form,
    Card,
    Alert,
    Progress,
    Tooltip
} from 'antd';
import {
    LockOutlined,
    EyeInvisibleOutlined,
    EyeTwoTone,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import useRequest from "@/utils/useRequest";

const SecurityTab = () => {
    const { request } = useRequest();
    const [form] = Form.useForm();
    
    const [loading, setLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        feedback: [],
        color: 'red',
        text: '弱'
    });
    
    // 密码强度检测
    const checkPasswordStrength = (password) => {
        if (!password) {
            setPasswordStrength({ score: 0, feedback: [], color: 'red', text: '弱' });
            return;
        }
        
        let score = 0;
        const feedback = [];
        
        // 长度检查
        if (password.length >= 8) {
            score += 25;
        } else {
            feedback.push('密码长度至少8位');
        }
        
        // 包含小写字母
        if (/[a-z]/.test(password)) {
            score += 25;
        } else {
            feedback.push('包含小写字母');
        }
        
        // 包含大写字母
        if (/[A-Z]/.test(password)) {
            score += 25;
        } else {
            feedback.push('包含大写字母');
        }
        
        // 包含数字
        if (/\d/.test(password)) {
            score += 25;
        } else {
            feedback.push('包含数字');
        }
        
        // 包含特殊字符
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            score += 10;
        } else {
            feedback.push('包含特殊字符(可选)');
        }
        
        let color, text;
        if (score < 50) {
            color = 'red';
            text = '弱';
        } else if (score < 75) {
            color = 'orange';
            text = '中';
        } else {
            color = 'green';
            text = '强';
        }
        
        setPasswordStrength({ score: Math.min(score, 100), feedback, color, text });
    };
    
    // 密码修改
    const changePassword = async (values) => {
        setLoading(true);
        try {
            const result = await request('/api/v2/private/user/password', 'POST', 'json', {
                oldpwd: values.oldPassword,
                newpwd: values.newPassword
            });
            
            if (result.success) {
                message.success('密码修改成功，请重新登录');
                form.resetFields();
                setPasswordStrength({ score: 0, feedback: [], color: 'red', text: '弱' });
                
                // 延迟跳转到登录页
                setTimeout(() => {
                    window.location.href = '/login';
                }, 2000);
            } else {
                message.error(result.message || '密码修改失败');
            }
        } catch (err) {
            console.error('密码修改错误:', err);
            message.error('密码修改失败，请重试');
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <Card>
            <div className="max-w-md mx-auto">
                <div className="text-center mb-6">
                    <LockOutlined className="text-4xl text-blue-500 mb-2" />
                    <h3 className="text-lg font-semibold">修改登录密码</h3>
                    <p className="text-gray-500 text-sm">为了您的账户安全，请定期更换密码</p>
                </div>
                
                <Alert
                    message="安全提示"
                    description="修改密码后需要重新登录，请确保记住新密码"
                    type="info"
                    showIcon
                    className="mb-6"
                />
                
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={changePassword}
                    className="space-y-4"
                >
                    <Form.Item
                        label="当前密码"
                        name="oldPassword"
                        rules={[
                            { required: true, message: '请输入当前密码' }
                        ]}
                    >
                        <Input.Password
                            placeholder="请输入当前密码"
                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        />
                    </Form.Item>
                    
                    <Form.Item
                        label="新密码"
                        name="newPassword"
                        rules={[
                            { required: true, message: '请输入新密码' },
                            { min: 8, message: '密码长度至少8位' },
                            {
                                validator: (_, value) => {
                                    if (value && passwordStrength.score < 50) {
                                        return Promise.reject(new Error('密码强度太弱，请设置更复杂的密码'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input.Password
                            placeholder="请输入新密码"
                            onChange={(e) => checkPasswordStrength(e.target.value)}
                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        />
                    </Form.Item>
                    
                    {/* 密码强度指示器 */}
                    {form.getFieldValue('newPassword') && (
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-gray-600">密码强度</span>
                                <span className={`text-sm font-medium text-${passwordStrength.color}-500`}>
                                    {passwordStrength.text}
                                </span>
                            </div>
                            <Progress
                                percent={passwordStrength.score}
                                strokeColor={{
                                    '0%': passwordStrength.color === 'red' ? '#ff4d4f' : 
                                          passwordStrength.color === 'orange' ? '#fa8c16' : '#52c41a',
                                    '100%': passwordStrength.color === 'red' ? '#ff4d4f' : 
                                            passwordStrength.color === 'orange' ? '#fa8c16' : '#52c41a'
                                }}
                                showInfo={false}
                                size="small"
                            />
                            {passwordStrength.feedback.length > 0 && (
                                <div className="mt-2">
                                    <p className="text-xs text-gray-500 mb-1">建议包含:</p>
                                    <div className="space-y-1">
                                        {passwordStrength.feedback.map((item, index) => (
                                            <div key={index} className="flex items-center text-xs text-gray-500">
                                                <CloseCircleOutlined className="text-red-400 mr-1" />
                                                {item}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <Form.Item
                        label="确认新密码"
                        name="confirmPassword"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: '请确认新密码' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('两次输入的密码不一致'));
                                }
                            })
                        ]}
                    >
                        <Input.Password
                            placeholder="请再次输入新密码"
                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        />
                    </Form.Item>
                    
                    <Form.Item>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={loading}
                            block
                            size="large"
                            className="mt-4"
                        >
                            修改密码
                        </Button>
                    </Form.Item>
                </Form>
                
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                        <InfoCircleOutlined className="mr-1" />
                        密码安全建议
                    </h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                        <li>• 使用至少8个字符的密码</li>
                        <li>• 包含大小写字母、数字和特殊字符</li>
                        <li>• 不要使用个人信息作为密码</li>
                        <li>• 定期更换密码，建议3-6个月更换一次</li>
                        <li>• 不要在多个网站使用相同密码</li>
                    </ul>
                </div>
            </div>
        </Card>
    );
};

export default SecurityTab;